import { Button } from '@/components/ui/button'
import { useDocuments } from '@/hooks/use-documents'
import { useSSEEvent } from '@/hooks/use-event-stream'
import { cn } from '@/lib/utils'
import { documentService } from '@/services/document.service'
import type { ChatMessage } from '@/stores/chat-store'
import type { DocumentStatus, RevisionReadyEvent } from '@/types'
import type { MessageJobContext, MessageMatchData } from '@/types'
import {
  AlertCircle,
  Briefcase,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Download,
  FileText,
  Headset,
  Link,
  Loader,
  XCircle,
} from 'lucide-react'
import { type ReactElement, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { filterHiddenData, parseRevisionData, stripIntentMarkers } from './chat-helpers'

// Message display types for component mapping
type MessageDisplayType = 'streaming' | 'resume_upload' | 'error' | 'assistant' | 'user'

export interface MessageRendererProps {
  msg: ChatMessage
  onRevisionGenerated?: (revision: RevisionReadyEvent) => void
}

// Resume preview data
interface ResumePreviewData {
  document_id: number
  focus_areas: string[]
  message_id: number
}

function getAssistantParagraphs(content: string): string[] {
  return content
    .split(/\n\s*\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
}

// Streaming message - iPhone-style typing indicator bubble
function StreamingMessageRenderer({ msg }: MessageRendererProps) {
  return (
    <div className="max-w-[90%] rounded-2xl rounded-bl-sm border border-border bg-muted px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-800/70">
      {msg.statusText ? (
        <div className="flex items-center gap-2">
          <Loader className="h-3.5 w-3.5 animate-spin text-muted-foreground dark:text-slate-400" />
          <span className="text-sm text-muted-foreground dark:text-slate-400">
            {msg.statusText}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <span className="sr-only" aria-live="polite">
            Coach is typing
          </span>
          <span
            className="h-2 w-2 rounded-full bg-muted-foreground/80 animate-bounce dark:bg-slate-300/80"
            style={{ animationDelay: '0ms', animationDuration: '900ms' }}
          />
          <span
            className="h-2 w-2 rounded-full bg-muted-foreground/80 animate-bounce dark:bg-slate-300/80"
            style={{ animationDelay: '150ms', animationDuration: '900ms' }}
          />
          <span
            className="h-2 w-2 rounded-full bg-muted-foreground/80 animate-bounce dark:bg-slate-300/80"
            style={{ animationDelay: '300ms', animationDuration: '900ms' }}
          />
        </div>
      )}
    </div>
  )
}

// Resume upload badge
interface ResumeUploadBadgeProps {
  filename: string
  status?: DocumentStatus | undefined
}

const statusConfig: Record<
  DocumentStatus,
  { icon: typeof CheckCircle2; color: string; bg: string; label: string }
> = {
  uploading: {
    icon: Loader,
    color: 'text-brand-600',
    bg: 'bg-brand-100 dark:bg-brand-800/30',
    label: 'Uploading...',
  },
  processing: {
    icon: Loader,
    color: 'text-brand-600',
    bg: 'bg-brand-100 dark:bg-brand-800/30',
    label: 'Processing...',
  },
  ready: {
    icon: CheckCircle2,
    color: 'text-success-600',
    bg: 'bg-success-100 dark:bg-green-900/30',
    label: 'Resume uploaded',
  },
  failed: {
    icon: XCircle,
    color: 'text-danger-600',
    bg: 'bg-danger-100 dark:bg-red-900/30',
    label: 'Upload failed',
  },
  invalid_document: {
    icon: AlertCircle,
    color: 'text-warning-600',
    bg: 'bg-warning-100 dark:bg-yellow-900/30',
    label: 'Invalid document',
  },
}

function ResumeUploadBadge({ filename, status }: ResumeUploadBadgeProps) {
  const config = statusConfig[status ?? 'failed']
  const StatusIcon = config.icon
  const isAnimated = status === 'uploading' || status === 'processing'

  return (
    <div className="flex items-center gap-3">
      <div
        className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', config.bg)}
      >
        <FileText className={cn('h-5 w-5', config.color)} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{filename}</p>
        <p className="text-xs text-muted-foreground">{config.label}</p>
      </div>
      <div className="flex items-center gap-1">
        <StatusIcon
          className={cn('h-4 w-4 shrink-0', config.color, isAnimated && 'animate-spin')}
        />
      </div>
    </div>
  )
}

function ResumeUploadRenderer({ msg }: MessageRendererProps) {
  const { data: documents } = useDocuments()
  const docId = msg.msg_metadata?.document_id
  const doc = documents?.find((d) => d.id === docId)

  return (
    <ResumeUploadBadge
      filename={msg.content.replace('Resume uploaded: ', '')}
      status={doc?.status}
    />
  )
}

// Custom components for ReactMarkdown
const markdownComponents = {
  // biome-ignore lint/suspicious/noExplicitAny: react-markdown component props
  code: ({ className, children, ...props }: any) => {
    const isInline = !className
    if (isInline) {
      return (
        <code
          className="bg-muted text-foreground px-1.5 py-0.5 rounded text-sm font-mono"
          {...props}
        >
          {children}
        </code>
      )
    }
    return (
      <code className={className} {...props}>
        {children}
      </code>
    )
  },
  // biome-ignore lint/suspicious/noExplicitAny: react-markdown component props
  pre: ({ children, ...props }: any) => (
    <pre
      className="bg-muted border border-border rounded-lg p-4 overflow-x-auto text-sm font-mono my-3 text-foreground"
      {...props}
    >
      {children}
    </pre>
  ),
}

// Resume download buttons component
interface ResumeDownloadButtonsProps {
  revision: RevisionReadyEvent
}

function ResumeDownloadButtons({ revision: initialRevision }: ResumeDownloadButtonsProps) {
  const [isDownloading, setIsDownloading] = useState<'pdf' | 'docx' | null>(null)
  const [status, setStatus] = useState(initialRevision.status)

  // Listen for SSE revision status changes instead of polling
  useSSEEvent('revision_status_changed', (event) => {
    const data = event.data as { revision_id: number; status: string }
    if (data.revision_id === initialRevision.revision_id) {
      setStatus(data.status as typeof status)
    }
  })

  const handleDownload = async (format: 'pdf' | 'docx') => {
    setIsDownloading(format)
    try {
      const result = await documentService.getRevisionDownloadUrl(
        initialRevision.document_id,
        initialRevision.revision_id,
        format,
      )
      window.open(result.download_url, '_blank')
    } catch (error) {
      console.error(`Failed to get ${format} download URL:`, error)
      alert(`Failed to download ${format.toUpperCase()}. Please try again.`)
    } finally {
      setIsDownloading(null)
    }
  }

  if (status === 'compiling' || status === 'generating') {
    return (
      <div className="mt-4 pt-4 border-t border-status-200 dark:border-status-800">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader className="h-4 w-4 animate-spin" />
          <span>
            Generating your improved resume (Version {initialRevision.revision_number})...
          </span>
        </div>
      </div>
    )
  }

  if (status === 'failed') {
    return (
      <div className="mt-4 pt-4 border-t border-danger-200 dark:border-red-800">
        <p className="text-sm text-danger-600 dark:text-red-400">
          Failed to generate resume. Please try again.
        </p>
      </div>
    )
  }

  return (
    <div className="mt-4 pt-4 border-t border-status-200 dark:border-status-800">
      <p className="text-sm font-medium text-muted-foreground mb-2">
        Your improved resume is ready (Version {initialRevision.revision_number})
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDownload('pdf')}
          disabled={isDownloading !== null}
          className="gap-2 dark:hover:bg-blue-950/50"
        >
          {isDownloading === 'pdf' ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Download PDF
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDownload('docx')}
          disabled={isDownloading !== null}
          className="gap-2 dark:hover:bg-blue-950/50"
        >
          {isDownloading === 'docx' ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Download DOCX
        </Button>
      </div>
    </div>
  )
}

// Resume preview card
interface ResumePreviewCardProps {
  preview: ResumePreviewData
  onRevisionGenerated: (revision: RevisionReadyEvent) => void
}

function ResumePreviewCard({ preview, onRevisionGenerated }: ResumePreviewCardProps) {
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleGenerate = async () => {
    setIsGenerating(true)
    setError(null)

    try {
      const revision = await documentService.generateRevision(preview.document_id, {
        focus_areas: preview.focus_areas,
        trigger: 'button',
      })

      onRevisionGenerated({
        document_id: preview.document_id,
        revision_id: revision.id,
        revision_number: revision.revision_number,
        status: revision.status,
      })
    } catch (err) {
      console.error('Failed to generate revision:', err)
      setError('Failed to generate resume. Please try again.')
    } finally {
      setIsGenerating(false)
    }
  }

  if (error) {
    return (
      <div className="mt-4 pt-4 border-t border-danger-200 dark:border-red-800">
        <p className="text-sm text-danger-600 dark:text-red-400 mb-2">{error}</p>
        <Button variant="outline" size="sm" onClick={handleGenerate} disabled={isGenerating}>
          Try Again
        </Button>
      </div>
    )
  }

  return (
    <div className="mt-4 pt-4 border-t border-status-200 dark:border-status-800">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-4 w-4 text-status-600 dark:text-status-500" />
        <span className="text-sm font-medium text-status-700 dark:text-status-300">
          Ready to generate your improved resume
        </span>
      </div>
      <div className="flex gap-2">
        <Button
          onClick={handleGenerate}
          disabled={isGenerating}
          className="gap-2 bg-gradient-to-r from-brand-600 to-brand-600 hover:from-brand-700 hover:to-brand-700"
          size="sm"
        >
          {isGenerating ? (
            <>
              <Loader className="h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              Generate PDF & DOCX
            </>
          )}
        </Button>
      </div>
      {preview.focus_areas.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          Focus: {preview.focus_areas.join(', ')}
        </p>
      )}
    </div>
  )
}

// Assistant message renderer
function AssistantMessageRenderer({ msg, onRevisionGenerated }: MessageRendererProps) {
  // Progress status messages render as compact indicators
  if (msg.msg_metadata?.type === 'progress_status') {
    return (
      <div className="flex items-center gap-2 py-1 max-w-[90%]">
        <Loader className="h-3 w-3 animate-spin text-muted-foreground" />
        <span className="text-xs text-muted-foreground italic">
          {filterHiddenData(stripIntentMarkers(msg.content))}
        </span>
      </div>
    )
  }

  const revision = msg.msg_metadata?.revision_data || msg.revision || parseRevisionData(msg.content)
  const displayContent = filterHiddenData(stripIntentMarkers(msg.content))
  const paragraphs = getAssistantParagraphs(displayContent)

  return (
    <div className="space-y-2 max-w-[90%]">
      {paragraphs.map((paragraph) => (
        <div
          key={paragraph}
          className={cn(
            'rounded-2xl rounded-bl-sm bg-gradient-to-br from-muted to-muted/70 px-4 py-2.5 text-black shadow-sm dark:text-white',
            msg.isStreaming && 'animate-imessage-bubble',
          )}
        >
          <div className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert break-words prose-p:text-black dark:prose-p:text-white prose-li:text-black dark:prose-li:text-white prose-strong:text-black dark:prose-strong:text-white">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              components={markdownComponents}
            >
              {paragraph}
            </ReactMarkdown>
          </div>
        </div>
      ))}
      {msg.isStreaming && !msg.isGeneratingResume && (
        <div className="pt-0.5">
          <StreamingMessageRenderer msg={msg} />
        </div>
      )}
      {msg.isGeneratingResume && (
        <div className="mt-3 flex items-center gap-2 text-sm text-status-600 dark:text-status-500">
          <Loader className="h-4 w-4 animate-spin" />
          <span>Updating resume preview...</span>
        </div>
      )}
      {revision && !msg.isStreaming && <ResumeDownloadButtons revision={revision} />}
      {msg.resumePreview &&
        !msg.isStreaming &&
        !msg.msg_metadata?.revision_data &&
        !msg.revision &&
        !parseRevisionData(msg.content) &&
        onRevisionGenerated && (
          <ResumePreviewCard
            preview={msg.resumePreview}
            onRevisionGenerated={onRevisionGenerated}
          />
        )}
    </div>
  )
}

// User message renderer
function UserMessageRenderer({ msg }: MessageRendererProps) {
  return (
    <div
      className={cn(
        'text-sm leading-relaxed prose prose-sm max-w-none break-words prose-p:text-black prose-li:text-black prose-strong:text-black text-black',
        msg.isNew && 'animate-imessage-bubble',
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownComponents}>
        {filterHiddenData(msg.content)}
      </ReactMarkdown>
    </div>
  )
}

// Error message renderer
function ErrorMessageRenderer({ msg }: MessageRendererProps) {
  if (msg.isRateLimitError) {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-2 text-danger-600 dark:text-red-400">
          <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span className="text-sm">{msg.content}</span>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={() => window.open('https://calendly.com/chay-pelgo/30min', '_blank')}
          >
            <Calendar className="h-4 w-4" />
            Schedule a Call
          </Button>
          <Button size="sm" variant="outline" className="gap-2" asChild>
            <a href="/connect">
              <Headset className="h-4 w-4" />
              Connect Live
            </a>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 text-danger-600 dark:text-red-400">
      <XCircle className="h-4 w-4 shrink-0" />
      <span className="text-sm">{msg.content}</span>
    </div>
  )
}

// Inline match card
function getScoreColor(score: number): string {
  if (score >= 80) return 'text-success-600'
  if (score >= 60) return 'text-warning-600'
  return 'text-danger-600'
}

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Strong Match'
  if (score >= 60) return 'Good Match'
  if (score >= 40) return 'Moderate Match'
  return 'Low Match'
}

export function InlineMatchCard({ matchData }: { matchData: MessageMatchData }) {
  const { score, breakdown, matchedSkills, missingSkills, jobTitle, company, companyLogoUrl } =
    matchData

  return (
    <div className="mt-2 max-w-[85%] rounded-lg border border-status-200 dark:border-status-800 bg-card dark:bg-gray-900 p-4 shadow-sm">
      {/* Company row: logo + name */}
      {company && (
        <div className="flex items-center gap-2 mb-2">
          {companyLogoUrl ? (
            <img
              src={companyLogoUrl}
              alt={`${company} logo`}
              className="h-6 w-6 rounded-md object-contain bg-muted border shrink-0"
              onError={(e) => {
                const target = e.currentTarget
                target.style.display = 'none'
                const fallback = target.nextElementSibling as HTMLElement
                if (fallback) fallback.style.display = 'flex'
              }}
            />
          ) : null}
          <div
            className={cn(
              'h-6 w-6 items-center justify-center rounded-md bg-muted dark:bg-gray-800 shrink-0',
              companyLogoUrl ? 'hidden' : 'flex',
            )}
          >
            <Briefcase className="h-3 w-3 text-muted-foreground" />
          </div>
          <span className="text-xs text-muted-foreground dark:text-gray-400 font-medium">
            {company}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-foreground dark:text-gray-100">
          {jobTitle || 'Match Analysis'}
        </div>
        <div className={`text-2xl font-bold ${getScoreColor(score)}`}>{score}%</div>
      </div>

      <div className="text-xs text-muted-foreground dark:text-gray-400 mb-3">
        {getScoreLabel(score)}
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        {breakdown.skills !== undefined && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Skills</span>
            <span className="font-medium">{breakdown.skills}%</span>
          </div>
        )}
        {breakdown.experience !== undefined && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Experience</span>
            <span className="font-medium">{breakdown.experience}%</span>
          </div>
        )}
        {breakdown.education !== undefined && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Education</span>
            <span className="font-medium">{breakdown.education}%</span>
          </div>
        )}
      </div>

      <div className="space-y-2 text-xs">
        {matchedSkills.length > 0 && (
          <div>
            <span className="text-success-600 font-medium">Matched: </span>
            <span className="text-muted-foreground dark:text-gray-400">
              {matchedSkills.slice(0, 4).join(', ')}
              {matchedSkills.length > 4 && ` +${matchedSkills.length - 4} more`}
            </span>
          </div>
        )}
        {missingSkills.length > 0 && (
          <div>
            <span className="text-danger-600 font-medium">Missing: </span>
            <span className="text-muted-foreground dark:text-gray-400">
              {missingSkills.slice(0, 4).join(', ')}
              {missingSkills.length > 4 && ` +${missingSkills.length - 4} more`}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// Inline job context chip
export function InlineJobChip({ jobContext }: { jobContext: MessageJobContext }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const { title, company, snippet, fullText, isUrl } = jobContext

  const summary = title
    ? company
      ? `${title} at ${company}`
      : title
    : company
      ? `Job at ${company}`
      : 'Job Posting'

  const expandedContent = fullText || snippet

  return (
    <div className="mt-2 max-w-[85%]">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left rounded-lg border border-brand-200 dark:border-brand-800 bg-brand-50 dark:bg-blue-950/30 px-3 py-2 hover:bg-brand-100 dark:hover:bg-brand-800/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isUrl ? (
            <Link className="h-4 w-4 text-brand-600 dark:text-brand-500 shrink-0" />
          ) : (
            <Briefcase className="h-4 w-4 text-brand-600 dark:text-brand-500 shrink-0" />
          )}
          <span className="text-xs font-medium text-brand-700 dark:text-brand-300 flex-1 truncate">
            {summary}
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-brand-500 dark:text-brand-500 shrink-0 transition-transform',
              isExpanded && 'rotate-180',
            )}
          />
        </div>

        {isExpanded && expandedContent && (
          <div className="mt-2 pt-2 border-t border-brand-200 dark:border-brand-700 max-h-64 overflow-y-auto">
            <div className="text-xs text-muted-foreground dark:text-gray-400 prose prose-xs max-w-none dark:prose-invert">
              <ReactMarkdown>{expandedContent}</ReactMarkdown>
            </div>
          </div>
        )}
      </button>
    </div>
  )
}

// Component map for message types
export const MESSAGE_RENDERERS: Record<
  MessageDisplayType,
  (props: MessageRendererProps) => ReactElement
> = {
  streaming: StreamingMessageRenderer,
  resume_upload: ResumeUploadRenderer,
  error: ErrorMessageRenderer,
  assistant: AssistantMessageRenderer,
  user: UserMessageRenderer,
}
